/* ──────────────────────────────────────────────────────────────
 *  Jobs API — background export job management
 *
 *  Handles starting export jobs and polling their status.
 *
 *  Path structure: {sessionId}/{jobId}/{sampleId}/{filename}
 *   - sessionId: Created when session loads
 *   - jobId: Created when Compute button is clicked (reused across
 *     every sample selection in that compute run)
 *   - sampleId: Updated when sample selection changes
 *   - filename: Generated file name
 *
 *  KEYING FIX: the backend job_manager is keyed by (job_id, sample_id),
 *  not job_id alone, since one job_id services many samples per compute
 *  run. getJobStatus and refreshDownloadUrl now both require sampleId.
 * ────────────────────────────────────────────────────────────── */

import axiosClient from './axiosClient';
import type { JobStatusResponse, StartExportJobPayload } from '@/types/job';

/** Start background export file generation after compute. */
export async function startExportJob(
  payload: StartExportJobPayload,
): Promise<{ job_id: string; status: string }> {
  const { data } = await axiosClient.post('/jobs/export', payload);
  return data;
}

/** Poll the status of a background export job for a specific sample. */
export async function getJobStatus(
  jobId: string,
  sampleId: string,
): Promise<JobStatusResponse> {
  const { data } = await axiosClient.get<JobStatusResponse>(`/jobs/${jobId}`, {
    params: { sample_id: sampleId },
  });
  return data;
}

/** Refresh an expired download URL for a file. */
export async function refreshDownloadUrl(
  sessionId: string,
  jobId: string,
  sampleId: string,
  filename: string,
): Promise<{ download_url: string; filename: string }> {
  const { data } = await axiosClient.post('/jobs/download-url', {
    session_id: sessionId,
    job_id: jobId,
    sample_id: sampleId,
    filename,
  });
  return data;
}